import React from 'react';
import { Copy, Building2, CreditCard, Hash, MapPin } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BUSINESS_INFO from '../config/businessInfo';
import { toast } from 'sonner';

const BankingDetailsPage = () => {
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  return (
    <div className="min-h-screen">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="text-center mb-12">
          <h1 className="font-playfair text-4xl sm:text-5xl font-bold text-[#2D2420] mb-4">
            Banking Details
          </h1>
          <p className="text-african-earth text-lg">
            For direct bank transfers (EFT payments)
          </p>
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-soft mb-8">
          <div className="space-y-6">
            {/* Bank Name */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-perfect-peach/10 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-6 h-6 text-perfect-peach" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-african-earth mb-1">Bank Name</p>
                <div className="flex items-center justify-between">
                  <p className="text-lg font-semibold text-[#2D2420]">
                    {BUSINESS_INFO.banking.bankName}
                  </p>
                  <button
                    onClick={() => copyToClipboard(BUSINESS_INFO.banking.bankName, 'Bank name')}
                    className="text-perfect-peach hover:text-[#C57D63] transition-colors duration-300"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Account Holder */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-royal-gold/10 flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-6 h-6 text-royal-gold" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-african-earth mb-1">Account Holder</p>
                <div className="flex items-center justify-between">
                  <p className="text-lg font-semibold text-[#2D2420]">
                    {BUSINESS_INFO.banking.accountHolder}
                  </p>
                  <button
                    onClick={() => copyToClipboard(BUSINESS_INFO.banking.accountHolder, 'Account holder')}
                    className="text-perfect-peach hover:text-[#C57D63] transition-colors duration-300"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Account Number */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-perfect-peach/10 flex items-center justify-center flex-shrink-0">
                <Hash className="w-6 h-6 text-perfect-peach" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-african-earth mb-1">Account Number</p>
                <div className="flex items-center justify-between">
                  <p className="text-lg font-semibold text-[#2D2420] font-mono">
                    {BUSINESS_INFO.banking.accountNumber}
                  </p>
                  <button
                    onClick={() => copyToClipboard(BUSINESS_INFO.banking.accountNumber, 'Account number')}
                    className="text-perfect-peach hover:text-[#C57D63] transition-colors duration-300"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Branch Code */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-african-earth/10 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-african-earth" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-african-earth mb-1">Branch Code</p>
                <div className="flex items-center justify-between">
                  <p className="text-lg font-semibold text-[#2D2420] font-mono">
                    {BUSINESS_INFO.banking.branchCode}
                  </p>
                  <button
                    onClick={() => copyToClipboard(BUSINESS_INFO.banking.branchCode, 'Branch code')}
                    className="text-perfect-peach hover:text-[#C57D63] transition-colors duration-300"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Account Type */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-royal-gold/10 flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-6 h-6 text-royal-gold" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-african-earth mb-1">Account Type</p>
                <p className="text-lg font-semibold text-[#2D2420]">
                  {BUSINESS_INFO.banking.accountType}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-bone-white rounded-3xl p-8">
          <h2 className="font-playfair text-2xl font-semibold text-[#2D2420] mb-4">
            Payment Instructions
          </h2>
          <ol className="space-y-3 text-african-earth">
            <li className="flex gap-3">
              <span className="font-bold text-perfect-peach">1.</span>
              <span>Make your payment using the bank details above</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-perfect-peach">2.</span>
              <span>
                Use your <strong>Order Number</strong> as the payment reference
              </span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-perfect-peach">3.</span>
              <span>
                Send proof of payment to{' '}
                <a
                  href={`mailto:${BUSINESS_INFO.email}`}
                  className="text-perfect-peach hover:underline"
                >
                  {BUSINESS_INFO.email}
                </a>
              </span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-perfect-peach">4.</span>
              <span>Your order will be processed within 1-2 business days after payment verification</span>
            </li>
          </ol>

          <div className="mt-6 p-4 bg-white rounded-xl border-l-4 border-perfect-peach">
            <p className="text-sm text-african-earth">
              <strong className="text-[#2D2420]">Note:</strong> For instant payment processing, 
              we recommend using our secure card payment option at checkout.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BankingDetailsPage;
